import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SavedSearches from './SavedSearches';
import UserManagement from './UserManagement';

interface UserProfile {
  id: string;
  cognito_user_id: string;
  email: string;
  username?: string;
  full_name?: string;
  role: string;
  department?: string;
  created_at: string;
  last_login?: string;
  is_active: boolean;
}

interface Bookmark {
  id: number;
  user_id: string;
  peid: number;
  title: string;
  notes?: string;
  folder_id?: number;
  created_at: string;
  updated_at: string;
}

interface Folder {
  id: number;
  user_id: string;
  folder_name: string;
  description?: string;
  icon?: string;
  color?: string;
  parent_folder_id?: number;
  is_default: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

const UserProfile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'bookmarks-folders' | 'searches' | 'profile' | 'users'>('bookmarks-folders');
  const [cognitoGroups, setCognitoGroups] = useState<string[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => {
    fetchUserProfile();
    loadCognitoGroups();
    if (activeTab === 'bookmarks-folders') {
      fetchFolders();
      fetchBookmarks();
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'bookmarks-folders') {
      fetchFolders();
      fetchBookmarks();
    }
  }, [activeTab]);

  const loadCognitoGroups = () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        setCognitoGroups(user.groups || []);
      }
    } catch (err) {
      console.error('Error loading Cognito groups:', err);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/api/user/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFolders(response.data);
    } catch (err: any) {
      console.error('Error fetching folders:', err);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/api/user/bookmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBookmarks(response.data);
    } catch (err: any) {
      console.error('Error fetching bookmarks:', err);
    }
  };

  const handleDeleteBookmark = async (bookmarkId: number) => {
    if (!window.confirm('Are you sure you want to delete this bookmark?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_BASE_URL}/api/user/bookmarks/${bookmarkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBookmarks(bookmarks.filter(bookmark => bookmark.id !== bookmarkId));
    } catch (err: any) {
      console.error('Error deleting bookmark:', err);
      alert('Failed to delete bookmark');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      alert('Please enter a folder name');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const folderData = {
        folder_name: newFolderName,
        description: '',
        icon: '📁',
        color: '#3b82f6'
      };
      
      await axios.post(`${API_BASE_URL}/api/user/folders`, folderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewFolderName('');
      setShowCreateFolderModal(false);
      fetchFolders(); // Refresh folders
    } catch (err: any) {
      console.error('Error creating folder:', err);
      alert(err.response?.data?.detail || 'Failed to create folder');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{backgroundColor: '#f3f4f6', minHeight: '100vh', padding: '2rem'}}>
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'}}>
          <div style={{fontSize: '1.5rem', color: '#111827', fontWeight: 'bold'}}>Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{backgroundColor: '#f3f4f6', minHeight: '100vh', padding: '2rem'}}>
        <div style={{backgroundColor: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '0.5rem', padding: '1.5rem', maxWidth: '500px', margin: '0 auto'}}>
          <div style={{color: '#991b1b', fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem'}}>Error: {error}</div>
          <button 
            onClick={fetchUserProfile}
            style={{padding: '0.75rem 1.5rem', backgroundColor: '#dc2626', color: 'white', borderRadius: '0.375rem', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: 'bold'}}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{backgroundColor: '#f3f4f6', minHeight: '100vh', padding: '2rem'}}>
        <div style={{textAlign: 'center', color: '#111827', fontSize: '1.5rem', fontWeight: 'bold'}}>No profile data available</div>
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', backgroundColor: '#f9fafb', padding: '0'}}>
      <div style={{maxWidth: '1200px', margin: '0 auto', padding: '2rem'}}>
        {/* Header */}
        <div style={{marginBottom: '2rem'}}>
          <h1 style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem'}}>User Profile</h1>
          <p style={{fontSize: '1.125rem', color: '#374151'}}>Manage your account settings and saved searches</p>
        </div>

        {/* Tab Navigation */}
        <div style={{borderBottom: '2px solid #d1d5db', marginBottom: '2rem'}}>
          <nav style={{display: 'flex', gap: '2rem'}}>
            <button
              onClick={() => setActiveTab('bookmarks-folders')}
              style={{
                padding: '0.75rem 0.5rem',
                borderBottom: activeTab === 'bookmarks-folders' ? '2px solid #2563eb' : '2px solid transparent',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                color: activeTab === 'bookmarks-folders' ? '#1d4ed8' : '#4b5563',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Bookmarks
            </button>
            <button
              onClick={() => setActiveTab('searches')}
              style={{
                padding: '0.75rem 0.5rem',
                borderBottom: activeTab === 'searches' ? '2px solid #2563eb' : '2px solid transparent',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                color: activeTab === 'searches' ? '#1d4ed8' : '#4b5563',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Searches
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              style={{
                padding: '0.75rem 0.5rem',
                borderBottom: activeTab === 'profile' ? '2px solid #2563eb' : '2px solid transparent',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                color: activeTab === 'profile' ? '#1d4ed8' : '#4b5563',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Profile
            </button>
            {cognitoGroups.includes('system-admins') && (
              <button
                onClick={() => setActiveTab('users')}
                style={{
                  padding: '0.75rem 0.5rem',
                  borderBottom: activeTab === 'users' ? '2px solid #2563eb' : '2px solid transparent',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  color: activeTab === 'users' ? '#1d4ed8' : '#4b5563',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Users
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'bookmarks-folders' && (
          <div style={{backgroundColor: 'white', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', borderRadius: '0.5rem', border: '1px solid #e5e7eb'}}>
            <div style={{padding: '2rem'}}>
              {/* Folders Top Row */}
              <div style={{marginBottom: '2rem'}}>
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginBottom: '1rem',
                  overflowX: 'auto',
                  paddingBottom: '0.5rem',
                  scrollBehavior: 'smooth'
                }}>
                  <button
                    onClick={() => setSelectedFolderId(null)}
                    style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: selectedFolderId === null ? '#3b82f6' : '#f3f4f6',
                      color: selectedFolderId === null ? 'white' : '#374151',
                      border: selectedFolderId === null ? 'none' : '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    📚 All Bookmarks ({bookmarks.length})
                  </button>
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => setSelectedFolderId(selectedFolderId === folder.id ? null : folder.id)}
                      style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: selectedFolderId === folder.id ? '#3b82f6' : folder.color ? `${folder.color}20` : '#f3f4f6',
                        color: selectedFolderId === folder.id ? 'white' : folder.color || '#374151',
                        border: selectedFolderId === folder.id ? 'none' : '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      <div style={{
                        fontSize: '1.2rem',
                        width: '24px',
                        height: '20px',
                        backgroundColor: folder.color || '#f59e0b',
                        borderRadius: '3px 3px 0 0',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '0.25rem'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: '-2px',
                          left: '0',
                          width: '8px',
                          height: '4px',
                          backgroundColor: folder.color || '#f59e0b',
                          borderRadius: '2px 2px 0 0'
                        }}></div>
                      </div>
                      {folder.folder_name} ({bookmarks.filter(b => b.folder_id === folder.id).length})
                    </button>
                  ))}
                  <button
                    onClick={() => setShowCreateFolderModal(true)}
                    style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }}
                  >
                    <span style={{fontSize: '1.2rem'}}>➕</span>
                    New Folder
                  </button>
                </div>
              </div>

              {/* Bookmarks Section */}
              <div>
                <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>
                  {selectedFolderId === null 
                    ? 'All Bookmarks'
                    : `${folders.find(f => f.id === selectedFolderId)?.folder_name || 'Folder'} Bookmarks`
                  }
                </h3>
                
                {(() => {
                  const filteredBookmarks = selectedFolderId === null 
                    ? bookmarks 
                    : bookmarks.filter(b => b.folder_id === selectedFolderId);
                  
                  if (filteredBookmarks.length === 0) {
                    return (
                      <div style={{textAlign: 'center', padding: '3rem 1rem'}}>
                        <div style={{fontSize: '3rem', marginBottom: '1rem'}}>📚</div>
                        <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>
                          {selectedFolderId === null ? 'No bookmarks yet' : 'No bookmarks in this folder'}
                        </h3>
                        <p style={{fontSize: '1rem', color: '#6b7280', marginBottom: '2rem'}}>
                          Start bookmarking budget items by clicking the star (⭐) button on search result cards.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      backgroundColor: 'white',
                      overflow: 'hidden'
                    }}>
                      {/* Table Header */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '40px 1fr 120px 120px 120px 80px 80px',
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        padding: '0.75rem',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        <div></div>
                        <div>TITLE & DESCRIPTION</div>
                        <div>PEID</div>
                        <div>BOOKMARKED</div>
                        <div>FOLDER</div>
                        <div>FAVORITE</div>
                        <div>ACTIONS</div>
                      </div>

                      {/* Table Body */}
                      {filteredBookmarks.map((bookmark, index) => (
                        <div key={bookmark.id} style={{
                          display: 'grid',
                          gridTemplateColumns: '40px 1fr 120px 120px 120px 80px 80px',
                          borderBottom: index < filteredBookmarks.length - 1 ? '1px solid #f1f5f9' : 'none',
                          padding: '1rem 0.75rem',
                          alignItems: 'center',
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f1f5f9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
                        }}
                        >
                          {/* Checkbox */}
                          <div>
                            <input 
                              type="checkbox" 
                              style={{
                                width: '16px',
                                height: '16px',
                                accentColor: '#3b82f6'
                              }}
                            />
                          </div>

                          {/* Title & Description */}
                          <div>
                            <h4 style={{
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#111827',
                              margin: 0,
                              marginBottom: '0.25rem',
                              lineHeight: '1.25'
                            }}>
                              {bookmark.title}
                            </h4>
                            {bookmark.notes && (
                              <p style={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                margin: 0,
                                lineHeight: '1.3',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {bookmark.notes}
                              </p>
                            )}
                          </div>

                          {/* PEID */}
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#374151',
                            fontWeight: '500'
                          }}>
                            {bookmark.peid}
                          </div>

                          {/* Bookmarked Date */}
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#6b7280'
                          }}>
                            {new Date(bookmark.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>

                          {/* Folder */}
                          <div>
                            {bookmark.folder_id ? (
                              <span style={{
                                backgroundColor: folders.find(f => f.id === bookmark.folder_id)?.color ? `${folders.find(f => f.id === bookmark.folder_id)?.color}20` : '#e5e7eb',
                                color: folders.find(f => f.id === bookmark.folder_id)?.color || '#374151',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.375rem',
                                fontSize: '0.625rem',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                <div style={{
                                  width: '12px',
                                  height: '10px',
                                  backgroundColor: folders.find(f => f.id === bookmark.folder_id)?.color || '#f59e0b',
                                  borderRadius: '2px 2px 0 0',
                                  position: 'relative'
                                }}>
                                  <div style={{
                                    position: 'absolute',
                                    top: '-1px',
                                    left: '0',
                                    width: '4px',
                                    height: '2px',
                                    backgroundColor: folders.find(f => f.id === bookmark.folder_id)?.color || '#f59e0b',
                                    borderRadius: '1px 1px 0 0'
                                  }}></div>
                                </div>
                                {folders.find(f => f.id === bookmark.folder_id)?.folder_name || 'Unknown'}
                              </span>
                            ) : (
                              <span style={{
                                fontSize: '0.625rem',
                                color: '#9ca3af'
                              }}>
                                No folder
                              </span>
                            )}
                          </div>

                          {/* Favorite */}
                          <div style={{ textAlign: 'center' }}>
                            <button style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '1.25rem',
                              color: '#fbbf24',
                              padding: '0.25rem'
                            }}>
                              ⭐
                            </button>
                          </div>

                          {/* Actions */}
                          <div style={{ textAlign: 'center' }}>
                            <button
                              onClick={() => handleDeleteBookmark(bookmark.id)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#fef2f2',
                                color: '#dc2626',
                                border: '1px solid #fecaca',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                fontSize: '0.625rem',
                                fontWeight: '600',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#dc2626';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#fef2f2';
                                e.currentTarget.style.color = '#dc2626';
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'searches' && (
          <SavedSearches />
        )}

        {activeTab === 'profile' && (
          <div style={{backgroundColor: 'white', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', borderRadius: '0.5rem', border: '1px solid #e5e7eb'}}>
            <div style={{padding: '2rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb'}}>
              <h2 style={{fontSize: '1.875rem', fontWeight: 'bold', color: '#111827'}}>Account Information</h2>
            </div>
            <div style={{padding: '2rem'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem'}}>
                <div>
                  <label style={{display: 'block', fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem'}}>
                    Email Address
                  </label>
                  <div style={{fontSize: '1rem', color: '#111827', backgroundColor: '#f3f4f6', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontWeight: '500'}}>
                    {profile.email}
                  </div>
                </div>
                <div>
                  <label style={{display: 'block', fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem'}}>
                    Username
                  </label>
                  <div style={{fontSize: '1rem', color: '#111827', backgroundColor: '#f3f4f6', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontWeight: '500'}}>
                    {profile.username || 'Not set'}
                  </div>
                </div>
                <div>
                  <label style={{display: 'block', fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem'}}>
                    Full Name
                  </label>
                  <div style={{fontSize: '1rem', color: '#111827', backgroundColor: '#f3f4f6', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontWeight: '500'}}>
                    {profile.full_name || 'Not set'}
                  </div>
                </div>
                <div>
                  <label style={{display: 'block', fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem'}}>
                    Role & Permissions
                  </label>
                  <div style={{backgroundColor: '#f3f4f6', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                      <span style={{textTransform: 'capitalize', fontWeight: 'bold', fontSize: '1.125rem', color: '#111827'}}>{profile.role}</span>
                      {profile.role === 'admin' && (
                        <span style={{display: 'inline-flex', alignItems: 'center', padding: '0.5rem 0.75rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 'bold', backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5'}}>
                          Admin Access
                        </span>
                      )}
                      {profile.role === 'user' && (
                        <span style={{display: 'inline-flex', alignItems: 'center', padding: '0.5rem 0.75rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 'bold', backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd'}}>
                          Standard User
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {cognitoGroups.length > 0 && (
                  <div className="lg:col-span-2">
                    <label className="block text-base font-semibold text-gray-800 mb-2">
                      Cognito Groups
                    </label>
                    <div className="bg-gray-100 px-4 py-3 rounded-lg border">
                      <div className="flex flex-wrap gap-3">
                        {cognitoGroups.map((group) => (
                          <span
                            key={group}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800 border border-purple-200"
                          >
                            {group}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-base font-semibold text-gray-800 mb-2">
                    Account Created
                  </label>
                  <div className="text-base text-gray-900 bg-gray-100 px-4 py-3 rounded-lg border">
                    {formatDate(profile.created_at)}
                  </div>
                </div>
                <div>
                  <label className="block text-base font-semibold text-gray-800 mb-2">
                    Last Login
                  </label>
                  <div className="text-base text-gray-900 bg-gray-100 px-4 py-3 rounded-lg border">
                    {formatDate(profile.last_login)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && cognitoGroups.includes('system-admins') && (
          <UserManagement />
        )}

        {/* Create Folder Modal */}
        {showCreateFolderModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>
                Create New Folder
              </h3>
              
              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 'bold', color: '#374151', marginBottom: '0.5rem'}}>
                  Folder Name
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateFolder();
                    }
                  }}
                />
              </div>

              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '1rem'}}>
                <button
                  onClick={() => {
                    setShowCreateFolderModal(false);
                    setNewFolderName('');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                >
                  Create Folder
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default UserProfile;